import React from 'react'
import {
  Box,
  Button,
  Fade,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined'
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import UploadFileIcon from '@mui/icons-material/UploadFile'

import AdminInternalNav from './AdminInternalNav'
import CrearCliente from '../containers/CrearCliente'

const colors = {
  ink: '#06185f',
  muted: '#4c5d86',
  blue: '#0059f6',
  line: '#d9e3f4',
}

function OrderIcon({ dir = 'desc' }) {
  const ArrowIcon = dir === 'desc' ? ArrowDownwardOutlinedIcon : ArrowUpwardOutlinedIcon
  return (
    <Box sx={{ position: 'relative', width: 24, height: 24, lineHeight: 0, color: 'inherit' }}>
      <ArrowIcon fontSize="small" sx={{ position: 'absolute', left: 0, top: 0, color: 'inherit' }} />
      <AccessTimeOutlinedIcon sx={{ position: 'absolute', right: -2, bottom: -2, fontSize: 14, opacity: 0.9, color: 'inherit' }} />
    </Box>
  )
}

const buttonBase = {
  minHeight: 36,
  px: 2,
  borderRadius: '8px',
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'none',
}

const blueOutlinedButton = {
  ...buttonBase,
  color: colors.blue,
  borderColor: 'rgba(0,89,246,0.55)',
  backgroundColor: 'rgba(255,255,255,0.82)',
  '&:hover': {
    borderColor: colors.blue,
    backgroundColor: 'rgba(0,89,246,0.06)',
  },
}

export default function ConsultasNoAsignHead({
  canAssign,
  sortOrder = 'desc',
  selectedCount = 0,
  selectedCliente = null,
  onChangeSort,
  onClose,
  onOpenAsignacion,
  onOpenClosed,
  onOpenClientes,
  onEditCliente,
  onDeleteCliente,
  onDeleteSelected,
  onOpenImportCsv,
  isFullScreen,
  onToggleFullScreen,
}) {
  const canEdit = selectedCount === 1 && selectedCliente
  const canDelete = selectedCount > 0

  const PrimaryRow = (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', md: 'center' }}
      spacing={2}
      sx={{ width: '100%' }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            color: colors.ink,
            fontSize: 26,
            fontWeight: 900,
            letterSpacing: '-0.7px',
            lineHeight: 1.1,
          }}
        >
          Consultas no asignadas
        </Typography>
        <Typography sx={{ mt: 0.5, color: colors.muted, fontSize: 12, fontWeight: 500 }}>
          Revisá, cargá y prepará consultas antes de asignarlas al equipo.
        </Typography>
      </Box>

      {!isFullScreen && (
        <Stack direction="row" alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} spacing={1} flexWrap="wrap" useFlexGap>
          <CrearCliente />

          <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => onOpenImportCsv?.()} sx={blueOutlinedButton}>
            Carga masiva
          </Button>

          <AdminInternalNav
            active="no_asignadas"
            onHome={onClose}
            onNoAsignadas={() => {}}
            onCerradas={onOpenClosed}
            onClientes={onOpenClientes}
          />
        </Stack>
      )}
    </Stack>
  )

  const SecondaryRow = (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', md: 'center' }}
      spacing={1.5}
      sx={{ width: '100%' }}
    >
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          disabled={!canAssign}
          startIcon={<AddIcon />}
          onClick={onOpenAsignacion}
          sx={{
            ...buttonBase,
            backgroundColor: colors.blue,
            boxShadow: '0 10px 22px rgba(0, 89, 246, 0.18)',
            '&:hover': {
              backgroundColor: '#004be0',
              boxShadow: '0 12px 26px rgba(0, 89, 246, 0.24)',
            },
          }}
        >
          Asignar
        </Button>

        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          disabled={!canEdit}
          onClick={() => onEditCliente?.(selectedCliente)}
          sx={blueOutlinedButton}
        >
          Editar
        </Button>

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          disabled={!canDelete}
          onClick={() => {
            if (selectedCount === 1 && selectedCliente) onDeleteCliente?.(selectedCliente)
            else if (selectedCount > 1) onDeleteSelected?.()
          }}
          sx={{
            ...buttonBase,
            backgroundColor: 'rgba(255,255,255,0.82)',
          }}
        >
          Borrar
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} spacing={1}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={sortOrder}
          onChange={(_, val) => {
            if (!val) return
            onChangeSort?.(val)
          }}
          sx={{
            '& .MuiToggleButton-root': {
              width: 38,
              height: 38,
              p: 0,
              color: colors.blue,
              borderColor: colors.line,
              backgroundColor: 'rgba(255,255,255,0.88)',
              '&.Mui-selected': {
                bgcolor: colors.blue,
                color: '#fff',
                borderColor: colors.blue,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#004be0', borderColor: '#004be0' },
              },
            },
          }}
        >
          <Tooltip title="Más nuevos primero" arrow>
            <ToggleButton value="desc" aria-label="orden-desc">
              <OrderIcon dir="desc" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Más antiguos primero" arrow>
            <ToggleButton value="asc" aria-label="orden-asc">
              <OrderIcon dir="asc" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        <Tooltip title={isFullScreen ? 'Salir de vista completa' : 'Vista completa'}>
          <IconButton
            onClick={() => onToggleFullScreen && onToggleFullScreen(!isFullScreen)}
            sx={{
              width: 38,
              height: 38,
              color: colors.blue,
              border: `1px solid ${colors.line}`,
              borderRadius: '8px',
              backgroundColor: 'rgba(255,255,255,0.88)',
              '&:hover': { backgroundColor: '#edf4ff' },
            }}
          >
            {isFullScreen ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  )

  if (isFullScreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: '60px',
          left: 0,
          width: '100%',
          zIndex: 1050,
          height: '72px',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
          '&:hover': { pointerEvents: 'auto' },
          '&:hover .MuiPaper-root': {
            transform: 'translateY(0)',
            opacity: 1,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '95%',
            mt: 1,
            px: 2,
            py: 1.2,
            borderRadius: '10px',
            bgcolor: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            transform: 'translateY(-120%)',
            opacity: 0,
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s',
            border: `1px solid ${colors.line}`,
            boxShadow: '0 18px 38px rgba(19, 39, 84, 0.14)',
            pointerEvents: 'auto',
          }}
        >
          {SecondaryRow}
        </Paper>

        <Fade in={true} timeout={1000}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '220px',
              height: '6px',
              bgcolor: colors.blue,
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
              opacity: 0.8,
              pointerEvents: 'auto',
              cursor: 'pointer',
              zIndex: 1290,
              boxShadow: '0 2px 4px rgba(0,0,0,0.16)',
            }}
          />
        </Fade>
      </Box>
    )
  }

  return (
    <Box sx={{ mb: 2.2 }}>
      <Stack spacing={2}>
        {PrimaryRow}
        {SecondaryRow}
      </Stack>
    </Box>
  )
}
